import track from 'react-tracking';
import { toCurrencyTruncated, toTruncated } from '@simplywallst/formatting';
import { Button } from '@simplywallst/ui-core';
import { DetailsLogo, DetailsSummary } from './styled';
import { SWS_LEGACY_HOST } from '@/constants';
import DeferRenderUntilIntersecting from '@components/DeferRenderUntilIntersecting';
import { pluralise } from '@utilities/pluralise';

interface DetailsProps {
  url?: string;
  listing: string;
  currencyISO: string;
  industryPrimaryName: string;
  industryTertiaryName: string;
  yearFounded: number;
  companyName: string;
  uniqueSymbol: string;
  tickerSymbol: string;
  exchangeSymbol: string;
  currencySymbol: string;
  marketCapListing: number | null;
  marketCapPrimary: number;
  sharesOutstanding: number;
  originalMarketCap: number | null;
  skipDefer: boolean;
}

const defaultProps: Partial<DetailsProps> = {
  listing: '',
  yearFounded: -1,
  industryPrimaryName: '',
  industryTertiaryName: '',
  companyName: '',
  uniqueSymbol: '',
  tickerSymbol: '',
  exchangeSymbol: '',
  currencySymbol: '',
  sharesOutstanding: -1,
  marketCapListing: null,
  marketCapPrimary: -1,
  originalMarketCap: null,
};

const Details = ({
  url,
  companyName,
  yearFounded,
  uniqueSymbol,
  tickerSymbol,
  exchangeSymbol,
  currencyISO,
  marketCapListing,
  marketCapPrimary,
  sharesOutstanding,
  originalMarketCap,
  industryPrimaryName,
  industryTertiaryName,
  tracking: { trackEvent },
  skipDefer,
}: DetailsProps & Tracking) => {
  return (
    <section>
      <DetailsLogo itemProp="logo" itemType="https://schema.org/ImageObject">
        <DeferRenderUntilIntersecting skipDefer={skipDefer}>
          {() => (
            <>
              <meta
                content={`${SWS_LEGACY_HOST}/api/company/image/${uniqueSymbol}/logo`}
                itemProp="url"
              />
              <img
                src={`${SWS_LEGACY_HOST}/api/company/image/${uniqueSymbol}/logo`}
                alt={`${companyName} logo`}
              />
            </>
          )}
        </DeferRenderUntilIntersecting>
      </DetailsLogo>
      <DetailsSummary>
        <li>
          Name: <span itemProp="legalName">{companyName}</span>
        </li>
        <li>
          Ticker: <span>{tickerSymbol}</span>
        </li>
        <li>
          Exchange: <span>{exchangeSymbol}</span>
        </li>
        <li>
          Founded: <span itemProp="foundingDate">{yearFounded}</span>
        </li>
        <li>
          Industry: <span itemProp="industry">{industryTertiaryName}</span>
        </li>
        <li>
          Sector: <span itemProp="sector">{industryPrimaryName}</span>
        </li>
        <li>
          <span>{`${pluralise(
            originalMarketCap,
            ' ',
            'Implied '
          )} Market Cap: `}</span>
          <span>{toCurrencyTruncated(marketCapPrimary, 3, currencyISO)}</span>
        </li>
        {originalMarketCap !== null && originalMarketCap > 0 && (
          <li>
            <span>Market Cap: </span>
            <span>
              {toCurrencyTruncated(originalMarketCap, 3, currencyISO)}
            </span>
          </li>
        )}
        {marketCapListing !== null && marketCapListing !== marketCapPrimary && (
          <li>
            <span>Listing Market Cap: </span>
            <span>{toCurrencyTruncated(marketCapListing, 3, currencyISO)}</span>
          </li>
        )}
        <li>
          <span>Shares outstanding: </span>
          <span>{toTruncated(sharesOutstanding)}</span>
        </li>
        {typeof url !== 'undefined' && url !== '' && (
          <li>
            <span>Website: </span>
            <Button
              as="a"
              styleType="strongLink"
              href={url}
              rel="nofollow noreferrer noopener"
              target="_blank"
              onClick={() =>
                trackEvent({
                  action: 'click',
                  modifier: 'website',
                })
              }
            >
              {url}
            </Button>
          </li>
        )}
      </DetailsSummary>
    </section>
  );
};

Details.defaultProps = defaultProps;

export default track({
  type: 'track',
})(Details);
